'use client';

import { useRef } from 'react';
import SearchPromptsButton from './SearchPromptsButton';

interface ImagePromptAreaProps {
  /** The prompt that produced the image on screen. */
  prompt: string;
  /** Called when the search button navigates away, so the viewer isn't left covering the results. */
  onNavigate?: () => void;
  className?: string;
}

/**
 * The prompt block shown over a fullscreen image: the text itself plus "search prompts for
 * this". Shared by every fullscreen viewer — the gallery modal and the collage preview —
 * so reading an image's prompt works the same wherever the image is opened.
 */
export default function ImagePromptArea({ prompt, onNavigate, className }: ImagePromptAreaProps) {
  const promptRef = useRef<HTMLParagraphElement>(null);

  /** The user's selection if it lies inside the prompt, else '' so the whole prompt is used. */
  const selectedPromptText = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim() ?? '';
    if (!text || !sel?.rangeCount || !promptRef.current) return '';
    return promptRef.current.contains(sel.getRangeAt(0).commonAncestorContainer) ? text : '';
  };

  return (
    <div className={`flex items-start gap-2 max-w-2xl ${className ?? ''}`}>
      <p
        ref={promptRef}
        className="text-white/80 text-sm whitespace-pre-wrap break-words max-h-24 overflow-y-auto"
      >
        {prompt}
      </p>
      {/* Reading a whole prompt is where "find the others like this" occurs. Highlight
          part of it first and the selection is searched instead of the whole thing. */}
      <SearchPromptsButton
        text={prompt}
        resolveText={selectedPromptText}
        onNavigate={onNavigate}
        className="shrink-0 p-1.5 rounded-lg text-white/50 hover:text-black hover:bg-[var(--accent)] transition-all cursor-pointer"
        title="Search prompts for this (or for the text you have selected)"
      />
    </div>
  );
}
